'use client';
import { useEffect, useRef, useMemo } from 'react';
import Link from 'next/link';
import Container from '@/components/layout/Container';
import Image from 'next/image';
import Headings from '@/components/heading/Headings';
import BlazeSlider from 'blaze-slider';
import 'blaze-slider/dist/blaze.css';

export default function ContentList({
  position,
  block,
  title,
  cta,
  cta_url,
  background_color
}) {
  const sliderInstance = useRef(null); // Store the BlazeSlider instance
  const elRef = useRef(null); // Store the slider element reference
  const { content } = block.classic;

  const sliderConfig = useMemo(
    () => ({
      all: {
        transitionDuration: 600,
        slideGap: '40px'
      },
      '(min-width: 1025px)': {
        slidesToShow: 4,
        loop: false
      },
      '(max-width: 1024px)': {
        slidesToShow: 2,
        loop: false
      },
      '(max-width: 640px)': {
        slidesToShow: 1,
        loop: false
      }
    }),
    []
  );

  useEffect(() => {
    if (!sliderInstance.current && elRef.current) {
      sliderInstance.current = new BlazeSlider(elRef.current, sliderConfig);
    }

    // Cleanup on unmount
    return () => {
      if (sliderInstance.current) {
        sliderInstance.current.destroy();
        sliderInstance.current = null; // Clear the reference
      }
    };
  }, [position, sliderConfig]);

  return (
    <section
      id={`content-list-${position}`}
      data-name={`content-list-${position}`}
      className={`bg-[${background_color}] content-list py-20`}
    >
      <Container fullPage={false}>
        <Headings
          headingContent={title}
          headingClassName="h4 sm:h3 lg:h2 text-center sm:mx-12 mb-8 sm:mb-15"
        />
        <div className="mx-auto block w-full flex-row flex-wrap justify-between px-1 sm:flex sm:px-0">
          <div className="blaze-slider" ref={elRef}>
            <div className="blaze-container">
              <div className="blaze-track-container">
                <ul className="blaze-track" tabIndex={0}>
                  {content?.map((item, index) => (
                    <li className="lg:flex-1" key={index}>
                      <div className="relative mx-auto">
                        <Image
                          src={`${item.image.url}?disable=upscale`}
                          width="600"
                          height="447"
                          sizes="(min-width: 1536px) 270px, (min-width: 1024px) 195px, (min-width: 768px) 324px, (min-width: 640px) 260px, 600px"
                          alt={item.image.title.replace(/\.[^/.]+$/, '')}
                          style={{ aspectRatio: 137 / 102 }}
                        />
                      </div>
                      <h3 className="sh10 sm:sh9 mb-2 pt-4 text-center">
                        {item.title}
                      </h3>
                      <p className="body-r mx-auto text-center">
                        {item.description}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="blaze-pagination indicators-dark mt-8 flex sm:mt-15 lg:mt-0"></div>
            </div>
          </div>
        </div>
        <CallToAction show={cta.show_cta} data={cta} cta_url={cta_url} />
      </Container>
    </section>
  );
}

function CallToAction({ show, data, cta_url }) {
  if (show && data.reference[0]) {
    return (
      <div className="pt-8 text-center sm:pt-15">
        <Link
          href={cta_url}
          prefetch={false}
          title={data.link_label}
          className="btn btn-tertiary-dark inline-block"
        >
          {data.link_label}
        </Link>
      </div>
    );
  }
  return null;
}
